/* eslint-disable no-warning-comments */

import classNames from 'classnames'
import {gsap} from 'gsap'
import {ScrollTrigger} from 'gsap/ScrollTrigger'
import * as React from 'react'

import IconMenuClose from '@/assets/specify/IconMenuClose'
import {makeEditable} from '@/common/utils'
import Button from '@/components/Button'
import Buttons from '@/components/Buttons'
import {Grid} from '@/components/Grid'
import Image from '@/components/Image'
import Link from '@/components/Link'

import InfosCard from './InfosCard'
import LogoLink from './LogoLink'
import NavLink from './NavLink'
import NavMobileMenu from './NavMobileMenu'
import RessourceCardLink from './RessourceCardLink'
import RessourceLink from './RessourceLink'
import RessourceLinks from './RessourceLinks'
import TopBar from './TopBar'
export const TOP_NAVIGATION_HEIGHT = '32px'
export const NAVIGATION_HEIGHT = '80px'

const NavigationV2 = ({
  _editable,
  logo,
  logoLink,
  loginLabel,
  loginLink,
  items,
  buttons,
  story,
}) => {
  const navRef = React.useRef()
  const backgroundRef = React.useRef()
  const navContainerRef = React.useRef()
  const navItemsContainerRef = React.useRef()
  const itemsColumnRef = React.useRef([])
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const [navItemsContainerHover, setNavItemsContainerHover] = React.useState(false)
  const [backgroundOpacity, setBackgroundOpacity] = React.useState(false)
  const [currentItemActiveIndex, setCurrentItemActiveIndex] = React.useState(0)

  const sectionRef = React.useRef(null)
  const [, setIsScrolling] = React.useState(false)
  const [subItemActive, setSubItemActive] = React.useState(0)
  const [menuItemActiveIndex, setMenuItemActiveIndex] = React.useState(0)
  const handleOpen = () => {
    setMobileMenuOpen(true)
    ScrollTrigger.normalizeScroll(false)
  }

  React.useEffect(() => {
    const nav = navRef.current
    const background = backgroundRef.current

    if (nav && background) {
      const context = gsap.context(() => {
        ScrollTrigger.create({
          trigger: nav,
          pin: nav,
          pinSpacing: false,
          start: 'top 8px',
          end: 'max',
          onEnter: () => {
            setBackgroundOpacity(true)
          },
          onLeaveBack: () => {
            setBackgroundOpacity(false)
          },
        })
      })

      return () => context.revert()
    }
  }, [])

  React.useEffect(() => {
    const tweens = []
    const context = gsap.context(() => {
      tweens.push(
        gsap
          .from(navContainerRef.current, {
            yPercent: -110,
            duration: 0.25,
          })
          .progress(1)
      )
      if (sectionRef.current !== null) {
        tweens.push(
          gsap.set(sectionRef.current, {
            top: 0,
          })
        )
        tweens.push(
          gsap.to(sectionRef.current, {
            scrollTrigger: {
              start: 'top+=150 top',
              end: 'bottom',
              scrub: true,
              onUpdate: (self) => {
                self.direction === -1 ? tweens[0].play() : tweens[0].reverse()
              },
            },
          })
        )
      }
    })
    return () => context.revert()
  }, [])

  const onScroll = () => {
    if (window.scrollY > 88) {
      setIsScrolling(true)
    } else setIsScrolling(false)
  }

  React.useEffect(() => {
    const context = gsap.context(() => {
      window.addEventListener('scroll', onScroll)
      return () => {
        window.removeEventListener('scroll', onScroll)
      }
    })
    return () => context.revert()
  }, [])

  return (
    <section
      className={classNames(
        'before:content-[" "] absolute left-0 top-0 z-10 w-full  before:absolute before:left-0 before:top-0 before:z-[11] before:h-full before:w-full',
        {
          ' bg-white transition-all duration-300 before:bg-white': navItemsContainerHover,
          'transition-all duration-300 before:bg-transparent': !navItemsContainerHover,
        }
      )}
      {...makeEditable(_editable)}
      ref={sectionRef}
    >
      <TopBar loginLabel={loginLabel} loginLink={loginLink} />
      <div style={{height: NAVIGATION_HEIGHT}} className='relative z-20'>
        <Grid className='relative'>
          <div ref={navRef} className='relative col-span-full flex items-center gap-1'>
            <div ref={navContainerRef} className='flex w-full items-center py-4'>
              <div
                ref={backgroundRef}
                className={classNames(
                  'transition-200 absolute -left-3 -right-3 block h-full rounded-[theme(spacing.10)] bg-white bg-opacity-60 backdrop-blur duration-150 md:-left-8 md:-right-8 lg:-left-10 lg:-right-10',
                  {
                    'opacity-100': backgroundOpacity,
                    'opacity-0': !backgroundOpacity,
                  }
                )}
              />
              <Link
                className='relative z-30 mr-12 flex-none text-purple-900 xl:text-left'
                link={logoLink}
              >
                <Image {...logo} ratio='9:2' sizes={{sm: '40', unit: 'px'}} />
              </Link>
              <nav className='relative z-20 flex-1 overflow-visible lg:-ml-2'>
                <div className='hidden items-center justify-between lg:flex'>
                  <div
                    className='flex'
                    ref={navItemsContainerRef}
                    onMouseEnter={() => {
                      setNavItemsContainerHover(true)
                    }}
                    onMouseLeave={() => {
                      setNavItemsContainerHover(false)
                      setMenuItemActiveIndex(null)
                      setCurrentItemActiveIndex(null)
                    }}
                  >
                    {items.map(
                      (
                        {
                          _uid,
                          label,
                          link,
                          highlightedItems = [],
                          items = [],
                          ressource,
                          infoCards,
                        },
                        index
                      ) => {
                        const subItems = items
                        const currentLinkItems = items.map((item) =>
                          item?.items?.find((el) =>
                            !el.link.story
                              ? el.link?.cached_url === story?.full_slug
                              : el.link.story && el.link?.story.full_slug === story?.full_slug
                          )
                        )

                        const currentLinkHighlighted = highlightedItems.find((el) =>
                          !el.link.story
                            ? el.link?.cached_url === story?.full_slug
                            : el.link.story && el.link?.story.full_slug === story?.full_slug
                        )

                        if (link && link.id) {
                          return (
                            <div
                              key={_uid}
                              className='group relative z-20 flex h-8 flex-col overflow-hidden'
                              onMouseEnter={() => {
                                setCurrentItemActiveIndex(index)
                              }}
                            >
                              <Link
                                link={link}
                                activeClassName='body-s-medium translate-y-0 rounded-[40px] bg-violet-600 px-3 py-1 text-white'
                                className='body-s-medium rounded-[40px] px-3 py-1'
                              >
                                {label}
                              </Link>
                              <Link
                                link={link}
                                activeClassName='hidden'
                                className='body-s-medium translate-y-0 rounded-[40px] bg-violet-600 px-3 py-1 text-white transition-all duration-300 ease-out group-hover:-translate-y-8'
                              >
                                {label}
                              </Link>
                            </div>
                          )
                        } else if (Array.isArray(items)) {
                          return (
                            <div
                              key={_uid + '_popover'}
                              as={React.Fragment}
                              className='relative'
                              unmount='false'
                            >
                              <div className='group'>
                                <div className='relative z-20 h-full hover:outline-none hover:outline-0'>
                                  <div
                                    className='nav-container flex h-[35px] flex-col gap-1 overflow-hidden'
                                    onMouseEnter={() => {
                                      setMenuItemActiveIndex(index)
                                      setCurrentItemActiveIndex(index)
                                    }}
                                  >
                                    <span className='body-s-medium rounded-[40px] px-3 py-1'>
                                      {label}
                                    </span>
                                    <span
                                      className={classNames(
                                        'body-s-medium z-20 cursor-pointer rounded-[40px] bg-violet-600 px-3 py-1 text-white transition-all duration-300 ease-out group-hover:-translate-y-[34px]',
                                        {
                                          '-translate-y-[34px]':
                                            currentLinkItems[0] ||
                                            currentLinkHighlighted ||
                                            currentItemActiveIndex === index,
                                        }
                                      )}
                                      onMouseLeave={() => {
                                        // setMenuItemActiveIndex(null)
                                      }}
                                    >
                                      {label}
                                    </span>
                                  </div>
                                </div>
                                <div
                                  key={subItems._uid}
                                  className={classNames(
                                    'full-width fixed -top-2 hidden justify-between gap-x-20 overflow-hidden rounded-2xl border-t border-purple-200 bg-white pb-12 pt-20 shadow-[0px_28px_68px_0px_rgba(0,_0,_0,_0.5)] group-hover:flex'
                                  )}
                                >
                                  <div className='absolute left-0 h-[1px] w-[100vw] bg-violet-200'></div>
                                  <Grid className=''>
                                    {index === 0 ? (
                                      <div className='relative col-span-3 flex flex-col pt-12 '>
                                        {subItems
                                          .filter(
                                            (subItem) =>
                                              subItem.component === 'navigation-item-column'
                                          )
                                          .map((subItem, index) => {
                                            return (
                                              <div key={subItem._uid} className='mb-6 w-full'>
                                                <div
                                                  className={classNames(
                                                    'body-m-medium after:content-[" "] relative z-10 cursor-pointer p-2 py-3 text-purple-900 transition-all duration-200 after:absolute after:right-0 after:top-[0] after:h-full after:w-[2px]',
                                                    {
                                                      'bg-violet-200 pl-6 text-purple-900 after:bg-violey-700 after:transition-all after:duration-500':
                                                        subItemActive === index,
                                                      'after:bg-transparent':
                                                        subItemActive !== index,
                                                    }
                                                  )}
                                                  onClick={() => setSubItemActive(index)}
                                                >
                                                  {subItem.heading}
                                                </div>
                                              </div>
                                            )
                                          })}
                                      </div>
                                    ) : null}
                                    {subItems
                                      .filter(
                                        (subItem) => subItem.component !== 'navigation-item-column'
                                      )
                                      .map((subItem, index) => {
                                        const component = subItem.component
                                        const links = subItem.links
                                        if (component === 'navigation-links-title-v2') {
                                          const buttons = subItem.button
                                          return (
                                            <div
                                              key={subItem._uid}
                                              className={classNames('relative flex pt-6', {
                                                'before:content-[" "] before:absolute before:right-5 before:top-0 before:h-[50vh] before:w-[2px] before:bg-violet-200':
                                                  index === 0,
                                                'col-span-7 flex-row before:!-right-16 ':
                                                  menuItemActiveIndex === 3 && index === 0,
                                                ' col-span-4 col-start-9 flex-col':
                                                  menuItemActiveIndex === 3 && index === 1,
                                                'col-span-4 flex-col justify-between gap-5 pr-8':
                                                  menuItemActiveIndex !== 3,
                                              })}
                                            >
                                              <div
                                                className={classNames('w-full', {
                                                  'flex flex-row gap-20':
                                                    menuItemActiveIndex === 3 && index === 0,
                                                  'flex flex-col gap-5': menuItemActiveIndex !== 3,
                                                })}
                                              >
                                                {subItem.heading && (
                                                  <div className='heading-xs-millik'>
                                                    {subItem.heading}
                                                  </div>
                                                )}
                                                {menuItemActiveIndex === 4 && index !== 0 ? (
                                                  <div className='flex flex-wrap'>
                                                    {links &&
                                                      links.map((link) => {
                                                        const style = link.style
                                                        if (style === 'icon') {
                                                          return <NavLink {...link} />
                                                        } else if (style === 'ressource') {
                                                          return <RessourceCardLink {...link} />
                                                        } else if (style === 'logo') {
                                                          return (
                                                            <div className='w-1/2'>
                                                              <LogoLink {...link} />
                                                            </div>
                                                          )
                                                        } else {
                                                          return <NavLink {...link} />
                                                        }
                                                      })}
                                                  </div>
                                                ) : (
                                                  links &&
                                                  links.map((link) => {
                                                    const style = link.style
                                                    if (style === 'icon') {
                                                      return <NavLink {...link} />
                                                    } else if (style === 'ressource') {
                                                      return <RessourceCardLink {...link} />
                                                    } else if (style === 'logo') {
                                                      return <LogoLink {...link} />
                                                    } else {
                                                      return <NavLink {...link} />
                                                    }
                                                  })
                                                )}
                                              </div>
                                              {buttons && buttons.length > 0 && (
                                                <div className='flex'>
                                                  {buttons.map((button) => (
                                                    <Button
                                                      {...button}
                                                      theme='secondary'
                                                      size='small'
                                                    />
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          )
                                        }
                                        return null
                                      })}
                                    {subItems && index === 0 && (
                                      <div className='relative col-span-6 flex flex-wrap pb-6 pt-6'>
                                        {subItems.map((subItem, index) => {
                                          const component = subItem.component
                                          if (component === 'navigation-item-column') {
                                            return (
                                              <div
                                                className={classNames(
                                                  'before:content-[" "] height-fit relative flex w-full flex-wrap pl-8 before:absolute before:-left-[18px] before:top-[-10vh] before:z-0 before:h-[120vh] before:w-[2px] before:bg-violet-200',
                                                  {
                                                    hidden: subItemActive !== index,
                                                  }
                                                )}
                                                ref={(el) => {
                                                  itemsColumnRef.current[index] = el
                                                }}
                                              >
                                                {subItem.items.map((subItem) => {
                                                  return (
                                                    <div key={subItem._uid} className=' w-1/2'>
                                                      <NavLink {...subItem} align='top' />
                                                    </div>
                                                  )
                                                })}
                                              </div>
                                            )
                                          }
                                          return null
                                        })}
                                      </div>
                                    )}
                                    {ressource &&
                                      ressource.length > 0 &&
                                      ressource.map((card) => {
                                        if (card.component === 'navigation-ressource-link') {
                                          return (
                                            <div
                                              key={card._uid}
                                              className='col-span-4 flex justify-end pt-6'
                                            >
                                              <div className='max-w-[300px]'>
                                                <RessourceLink {...card} />
                                              </div>
                                            </div>
                                          )
                                        } else if (
                                          card.component === 'navigation-ressource-links'
                                        ) {
                                          return (
                                            <div
                                              key={card._uid}
                                              className={classNames('', {
                                                'col-span-3 pt-6': index === 0,
                                                'col-span-4 pt-4': index !== 0,
                                              })}
                                            >
                                              <div className='max-w-[300px]'>
                                                <RessourceLinks card={card} />
                                              </div>
                                            </div>
                                          )
                                        }
                                        return null
                                      })}
                                    {infoCards?.length > 0 && (
                                      <div className=' infocardcontainer relative col-span-full flex border-t border-purple-200 bg-white pt-6'>
                                        <Grid>
                                          <div className='col-span-full'>
                                            <div className='flex gap-5'>
                                              {infoCards.map((card) => (
                                                <InfosCard key={card._uid} card={card} />
                                              ))}
                                            </div>
                                          </div>
                                        </Grid>
                                      </div>
                                    )}
                                  </Grid>
                                </div>
                              </div>
                            </div>
                          )
                        }
                        return null
                      }
                    )}
                  </div>
                  <div className='flex items-center gap-4'>
                    <Buttons
                      buttons={buttons}
                      buttonProps={{hasIcon: false, size: 'small'}}
                      className='flex gap-2'
                    />
                  </div>
                </div>
                <div className='flex items-center justify-end gap-4 lg:hidden'>
                  <button
                    type='button'
                    className='h-10 w-10 rounded-full bg-violet-600 p-2'
                    onClick={handleOpen}
                  >
                    <span className='sr-only'>Open main menu</span>
                    <span aria-hidden='true' className='text-white'>
                      <IconMenuClose />
                    </span>
                  </button>
                </div>
              </nav>
              <NavMobileMenu
                logoLink={logoLink}
                items={items}
                buttons={buttons}
                mobileMenuOpen={mobileMenuOpen}
                setMobileMenuOpen={setMobileMenuOpen}
              />
            </div>
          </div>
        </Grid>
      </div>
    </section>
  )
}

export default NavigationV2
